import { Box, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV2";
import { format, isValid, parseISO } from "date-fns";

function toDayString(value) {
    if (!value || !isValid(value)) return "";
    return format(value, "yyyy-MM-dd");
}

function fromDayString(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
}

const fieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: "#ffffff",
        fontSize: 14,
        "& fieldset": {
            borderColor: "#94a3b8",
            borderWidth: 1,
        },
        "&:hover fieldset": {
            borderColor: "#64748b",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0d9488",
            borderWidth: 1.5,
        },
    },
    "& .MuiOutlinedInput-input": {
        py: "8.5px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#0d9488",
    },
};

const stopMenuClose = {
    onMouseDown: (event) => event.stopPropagation(),
};

const pickerSlotProps = {
    textField: {
        size: "small",
        fullWidth: true,
    },
    popper: {
        ...stopMenuClose,
        sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
    },
    desktopPaper: stopMenuClose,
    dialog: stopMenuClose,
};

/**
 * Compact From/To date fields shared across Pull menus and Reporting toolbars.
 * Values are yyyy-MM-dd strings.
 *
 * layout: "stack" (menu column, default) | "row" (inline toolbar)
 */
export default function PullDateRangeFields({
    startDate,
    endDate,
    onStartChange,
    onEndChange,
    error = "",
    hint = "",
    layout = "stack",
}) {
    const startValue = fromDayString(startDate);
    const endValue = fromDayString(endDate);
    const isRow = layout === "row";

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
                sx={{
                    px: isRow ? 0 : 1.5,
                    pt: isRow ? 0 : 1.5,
                    pb: isRow ? 0 : 1,
                    display: "flex",
                    flexDirection: isRow ? "row" : "column",
                    flexWrap: isRow ? "wrap" : "nowrap",
                    alignItems: isRow ? "flex-start" : "stretch",
                    gap: isRow ? 1.5 : 1.25,
                    minWidth: isRow ? 0 : 268,
                }}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <DatePicker
                    label="From"
                    value={startValue}
                    onChange={(next) => onStartChange(toDayString(next))}
                    maxDate={endValue || undefined}
                    format="MMM d, yyyy"
                    sx={{ ...fieldSx, ...(isRow ? { width: 168 } : null) }}
                    slotProps={pickerSlotProps}
                />
                <DatePicker
                    label="To"
                    value={endValue}
                    onChange={(next) => onEndChange(toDayString(next))}
                    minDate={startValue || undefined}
                    format="MMM d, yyyy"
                    sx={{ ...fieldSx, ...(isRow ? { width: 168 } : null) }}
                    slotProps={pickerSlotProps}
                />
                {error ? (
                    <Typography
                        sx={{
                            color: "#b91c1c",
                            fontSize: 12,
                            lineHeight: 1.35,
                            ...(isRow ? { alignSelf: "center", maxWidth: 220 } : null),
                        }}
                    >
                        {error}
                    </Typography>
                ) : hint ? (
                    <Typography
                        sx={{
                            color: "#64748b",
                            fontSize: 12,
                            lineHeight: 1.35,
                            ...(isRow ? { alignSelf: "center", maxWidth: 260 } : null),
                        }}
                    >
                        {hint}
                    </Typography>
                ) : null}
            </Box>
        </LocalizationProvider>
    );
}
